import { BotTasks, useQueryBotTask } from "@apis:bot_tasks/bot_tasks";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { Results } from "@pages:app/audio/components/Results";
import { botTaskAudioSelector } from "@pages:app/audio/components/selector";
import { FountainBlue } from "@themes:palette";
import { get } from "lodash";
import { useState } from "react";
import { IoMdSearch } from "react-icons/io";
import { MdCancel } from "react-icons/md";

interface StatusTabProps {
  sx?: SxProps;
}

export const StatusTab = ({ sx }: StatusTabProps) => {
  const [taskId, setTaskId] = useState("");
  const [taskStatus, setTaskStatus] = useState<
    BotTasks["GET"]["Response"] | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  const { refetch: fetchBotTask } = useQueryBotTask(taskId, {
    enabled: false,
    select: botTaskAudioSelector,
    throwOnError: false,
  });

  const checkStatus = async () => {
    if (!taskId.trim()) {
      setError("Please enter a task ID");
      return;
    }

    setIsLoading(true);
    setError("");
    setTaskStatus(undefined);

    try {
      const { data } = await fetchBotTask({ throwOnError: true });
      setTaskStatus(data);
      setIsLoading(false);
    } catch (error) {
      const errorMessage = get(
        error,
        "response.data.message",
        "Task not found or an error occurred",
      );
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Paper elevation={5} sx={{ p: 4, borderRadius: 4, ...sx }}>
      <Stack spacing={2}>
        <SuperInput.TextField
          sx={{
            marginTop: "1rem",
          }}
          label="Enter Task ID"
          value={taskId}
          placeholder="e.g., 318EA2B0-40E7-11F0-88C0-72D8D592A5F0"
          onChange={(e) => setTaskId(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IoMdSearch />
                </InputAdornment>
              ),
            },
          }}
        />

        {error && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: "error.main" }}
          >
            <MdCancel fontSize="large" />
            <Typography variant="body2">{error}</Typography>
          </Stack>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={checkStatus}
          disabled={isLoading || !taskId}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <IoMdSearch />
            )
          }
          sx={{ py: 2.5 }}
        >
          {isLoading ? "Checking..." : "Check Status"}
        </Button>

        {taskStatus && (
          <Box sx={{ mt: 3 }}>
            <Stack spacing={3}>
              <Stack
                direction="row"
                spacing={20}
                sx={{
                  bgcolor: "rgba(0, 0, 0, 0.03)",
                  border: "1px solid",
                  borderColor: "divider",
                  alignItems: "center",
                  p: "10px",
                  borderRadius: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Task ID
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        "&:hover": {
                          color: `${FountainBlue["500"]}!important`,
                        },
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        await handleCopy(taskStatus.id);
                      }}
                    >
                      {taskStatus.id}
                    </Typography>
                    <SVGInternalIcon
                      icon="copiedIcon"
                      sx={{
                        visibility: isCopied ? "visible" : "hidden",
                        fontSize: "small",
                        color: FountainBlue["500"],
                      }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    {taskStatus.status === "submitted" && (
                      <>
                        <SVGInternalIcon
                          icon="time"
                          sx={{ color: "orange", fontSize: "medium" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Waiting in queue
                        </Typography>
                      </>
                    )}
                    {taskStatus.status === "started" && (
                      <>
                        <CircularProgress size={15} />
                        <Typography variant="body2" color="text.secondary">
                          Audio processing in progress
                        </Typography>
                      </>
                    )}
                    {taskStatus.status === "completed" && (
                      <>
                        <SVGInternalIcon
                          icon="circleCheck"
                          sx={{ color: "green", fontSize: "medium" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Transcription is ready
                        </Typography>
                      </>
                    )}
                    {taskStatus.status === "failed" && (
                      <>
                        <SVGInternalIcon
                          icon="outlineCancel"
                          sx={{ color: "red", fontSize: "medium" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          An error occurred during processing
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>
              </Stack>

              {taskStatus.status === "completed" && (
                <Results
                  transcription={get(taskStatus, "transcription")}
                  diarization={get(taskStatus, "diarization")}
                />
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
